import { Transition } from "@mantine/core";
import { useRouter }  from "next/router";
import {
	forwardRef,
	useEffect,
	useRef,
	useState
} from "react";
import { Waypoint } from "react-waypoint";

import { useGetLoggedIn } from "_common/hooks/auth";
import {
	useGetDeviceType,
	useSetLoginPopup
} from "_common/hooks/global";
import { usePointerEvents }          from "_common/hooks/usePointerEvents";
import toast                         from "_common/toast";
import Icon                          from "_components/atoms/icon";
import Render                        from "_components/atoms/render";
import Typography                    from "_components/atoms/typography";
import usePlayback                   from "_components/features/player/hooks/usePlayback";
import LoginWall                     from "_components/molecules/loginWall";
import ControlsContainer             from "_components/pages/reels/components/controlsContainer";
import InfoOverlay                   from "_components/pages/reels/components/infoOverlay";
import VideoContainer                from "_components/pages/reels/components/videoContainer";
import { useGetCanWatchMoreContent } from "_jotai/compulsoryLogin";
import { postLikeOnFeed }            from "_services/api/feed";

import useStyles from "./styles";

const component =  forwardRef ( ( {
	reel,
	index,
	fetchMoreReels,
	length
}, ref ) => {
	const { classes }                    = useStyles ();
	const [ isReelReported, reportReel ] = useState ( false );
	const [ fadeHeader, setFadeHeader ]  = useState ();

	const _fetchMoreReels = () => {
		if ( index === length - 4 ) {
			fetchMoreReels ();
		}
	};

	useEffect ( () => {
		// reportedReelRef.current?.scrollIntoView ( {
		// behavior : "smooth",
		// block    : "center",
		// inline   : "center"
		// } );
		if ( isReelReported )
			toast.info ( "Reel Reported succesfully" );
	}, [ isReelReported ] );

	function updateReel () {
		reportReel ( true );
	}

	return (
		<Waypoint onEnter = { _fetchMoreReels }>

			<div className = { classes.wrapper }>
				<FirstReelHeader
					fadeHeader = { fadeHeader }
					id         = { reel.id }
					index      = { index }
				/>

				<div
					ref         = { ref }
					className   = { classes.reelOuterContainer }
					data-reelid = { reel.id }
					id          = { index }
				>
					<Render condition = { reel.reactions }>
						<ReelInnerContainer
							index          = { index }
							isReelReported = { isReelReported }
							reel           = { reel }
							setFadeHeader  = { setFadeHeader }
							updateReel     = { updateReel }
						/>
					</Render>
				</div>
			</div>

		</Waypoint>
	);
} );

component.displayName = component;

export default component;

function ReelInnerContainer ( { index, reel, updateReel, setFadeHeader, isReelReported } ) {
	const setLoginPopup                   = useSetLoginPopup ();
	const isLoggedIn                      = useGetLoggedIn ();
	const [ isReelLiked, setIsReelLiked ] = useState ( reel.isReactedOnFeed );
	const { classes }                     = useStyles ();
	const router                          = useRouter ();
	const componentRef                    = useRef ( null );
	const { togglePlayback }              = usePlayback ( );
	const deviceType                      = useGetDeviceType ();
	const isDesktop                       = deviceType === "desktop";
	const isMobile                        = deviceType === "mobile";

	const onClick = () => {
		togglePlayback ();
	};

	const sendReactionsOnVideo = async id => {
		try {
			await postLikeOnFeed ( { feedId: id } );

			setIsReelLiked ( true );

		} catch ( err ) {
			setIsReelLiked ( false );
			toast.error ( err.displayMsg );
		}
	};

	const onDoubleClick = () => {
		if ( !isLoggedIn ) {
			return setLoginPopup ( true );
		}
		if ( reel.isReactedOnFeed )
			return;

		sendReactionsOnVideo ( reel.id );
	};

	const onMouseHold = () => {
		setFadeHeader ( true );
	};

	const {
		pointerState,
		handlePointerDown,
		handlePointerUp
		// handleDoubleClick
	} = usePointerEvents ( {
		onClick,
		onDoubleClick,
		onMouseHold
	} );

	const events = {
		onDoubleClick: e => {
			// handleDoubleClick ( e );
		}
	};

	if ( isDesktop ) {
		events.onMouseDown = () => {
			handlePointerDown ();
		},
		events.onMouseUp = () => {
			setFadeHeader ( false );
			handlePointerUp ();
		};
	} else {
		events.onTouchEnd   = e => {
			setFadeHeader ( false );
			handlePointerUp ();
			e.preventDefault ();
		};
		events.onTouchStart = e => {
			handlePointerDown ();
			e.preventDefault ();
		};
	}

	return (
		<div className = { classes.innerContainer }>
			<div
				ref            = { componentRef }
				className      = { classes.videoContainerWrapper }
				// onContextMenu = { e => e.preventDefault () }
				// onDoubleClick = { handleDoubleClick }
				onMouseDown    = { () => {
					if ( isMobile ) return;
					handlePointerDown ();
				} }
				onMouseUp      = { () => {
					if ( isMobile ) return;
					setFadeHeader ( false );
					handlePointerUp ();
				} }
				onTouchEnd     = { () => {
					if ( isDesktop ) return;
					setFadeHeader ( false );
					handlePointerUp ();
				} }
				onTouchStart   = { () => {
					if ( isDesktop ) return;
					handlePointerDown ();
				} }
			>
				<LoginWall
					background = { reel.media.find ( item => item.mediaSequence === "7" )?.href }
					isReel     = { true }
					zIndex     = { 1 }
				>
					<VideoContainer
						fadeReelControls = { pointerState !== "pointerHold" }
						index            = { index }
						reel             = { reel }
					/>
				</LoginWall>

				<Transition
					duration       = { 400 }
					mounted        = { pointerState !== "pointerHold" }
					timingFunction = "ease"
					transition     = "fade"
				>
					{styles => (
						<div style = { styles }>
							<InfoOverlay
								key    = { reel.id }
								inView = { router.query.id.toString () === reel.id.toString () }
								reel   = { reel }
							/>

						</div>
					)}
				</Transition>
			</div>

			<Transition
				duration       = { 400 }
				mounted        = { pointerState !== "pointerHold" }
				timingFunction = "ease"
				transition     = "fade"
			>
				{styles => (
					<div style = { styles }>
						<ControlsContainer
							commentCount   = { reel.commentCount }
							isReelLiked    = { isReelLiked }
							likeCount      = { reel.reactions[0].count }
							reel           = { reel }
							reelLiked      = { isReelLiked }
							setIsReelLiked = { setIsReelLiked }
							shareCount     = { reel.sharesCount }
							updateFeedList = { updateReel }
						/>

					</div>
				)}
			</Transition>

		</div>
	);
}

const ReelReportedComponent = forwardRef ( ( _, ref ) => {
	const { classes } = useStyles ();

	return (
		<div ref       = { ref }
			className     = { classes.reportedComponnetRoot }
		>
			<div className = { classes.inner }>
				<Icon
					sameInBothTheme
					name  = "blueTick"
					size  = "54px"
				/>

				<Typography
					className = { classes.title }
					title     = { "Thanks for letting us know" }
				/>

				<Typography
					className = { classes.subtitle }
					title     = { "Your feedback improves the quality of feeds on Rooter" }
				/>
			</div>
		</div>
	);
} );

function FirstReelHeader ( { fadeHeader, index, id } ) {
	const deviceType  = useGetDeviceType ();
	const router      = useRouter ();
	const { classes } = useStyles ();

	return (
		<Transition
			duration       = { 400 }
			mounted        = { !fadeHeader }
			timingFunction = "ease"
			transition     = "fade"
		>
			{
				styles => {
					return (
						<div className = { classes.reelsIconWrapper }
							style         = { styles }
						>
							<div className = { classes.test }
								data-show     = { index === 0 || undefined }
							>
							</div>

							<Render condition = { deviceType === "mobile" }>
								<Icon
									name            = { "backArrow" }
									onClick         = { router.back }
									sameInBothTheme = { true }
									style           = { {
										width: "2.6rem"
									} }
								/>

							</Render>

							<Render condition = { index === 0 && id !== "customid" }>
								<div className = { classes.headerText }>

									<Icon sameInBothTheme
										className = { classes.reelIcon }
										name      = { "reel-shorts-bytes" }
									/>

									<Typography className = { classes.reelText }
										title                = "Reels"
									/>
								</div>
							</Render>

						</div>
					);
				}
			}

		</Transition>
	);
}
ReelReportedComponent.displayName = "ReelReportedComponent";
